import { useState, type ReactNode } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface RelatedColumn<T> {
  key: string;
  render: (row: T) => ReactNode;
}

export interface QuickAddAction {
  ariaLabel: string;
  onClick: () => void;
}

export interface RelatedTableProps<T> {
  icon: ReactNode;
  title: string;
  count: number;
  columns: RelatedColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  quickAdd?: QuickAddAction;
  emptyText?: string;
  limit?: number;
}

export function RelatedTable<T>({
  icon,
  title,
  count,
  columns,
  rows,
  getRowKey,
  onRowClick,
  quickAdd,
  emptyText = "Nothing here yet.",
  limit = 5,
}: RelatedTableProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = rows.length > limit;
  const visible = expanded ? rows : rows.slice(0, limit);

  return (
    <div className="overflow-hidden rounded-[10px] border bg-card">
      <div className="flex items-center justify-between px-4 pb-2 pt-3.5">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold">
          <span className="text-muted-foreground">{icon}</span>
          {title} ({count})
        </h2>
        {quickAdd && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            aria-label={quickAdd.ariaLabel}
            onClick={quickAdd.onClick}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <p className="border-t px-4 py-3 text-center text-xs text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        <>
          {visible.map((row) => (
            <div
              key={getRowKey(row)}
              role={onRowClick ? "button" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "flex items-center gap-2 border-t px-4 py-2 text-sm",
                onRowClick &&
                  "cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
            >
              {columns.map((column, index) => (
                <div
                  key={column.key}
                  className={index === 0 ? "min-w-0 flex-1" : "shrink-0"}
                >
                  {column.render(row)}
                </div>
              ))}
            </div>
          ))}

          {hasOverflow && (
            <div className="border-t px-4 py-2">
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {expanded ? "Show less" : `Show all (${rows.length})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
